import { utcDayBounds, utcDayKey } from "@/lib/calendarDay";

export type OccurrenceState = "past" | "excluded" | "available";

export type OccurrenceEntry = {
  iso: string;
  date: Date;
  state: OccurrenceState;
};

export type OccurrenceRecurrenceInput = {
  frequency?: "daily" | "weekly" | "monthly" | null;
  interval?: number | null;
  count?: number | null;
  until?: Date | string | null;
  // A from-date cancellation / end-membership truncation: occurrences ON/AFTER this day stop. Slots
  // are bounded ONLY this way (R13 strips their `until`), so without it a terminated member's slot
  // enumerates 52 phantom occurrences past the termination date.
  cancelledFrom?: Date | string | null;
  byWeekday?: string[] | null;
  exceptions?: (Date | string)[] | null;
};

const FORWARD_LOOKUP_LIMIT = 52;
const PAST_TAIL_FOR_OPEN_ENDED = 3;
// R1 — Members can never look further than one year forward, so we cap the rolling window at
// 365 days for unbounded recurrences too.
const ONE_YEAR_DAYS = 365;
// Hard safety ceiling so a runaway recurrence config never spins forever.
const MAX_ITERATIONS = 1024;

// Step `date` forward by `days` UTC calendar days, preserving its UTC time-of-day. Built from UTC
// calendar fields (not added milliseconds) so month/year always roll over correctly and the result
// never disagrees with the UTC-anchored day identity the rest of this file uses (see
// @/lib/calendarDay and BookingOccurrenceList.enumerateOccurrences, which steps the same way).
const addUtcDays = (date: Date, days: number): Date =>
  new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() + days,
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds(),
    ),
  );

// Step `date` forward by `months` UTC calendar months, clamping the day-of-month to the last day of
// the target month when it doesn't exist (e.g. 31 Jan + 1 month → 28/29 Feb) — the same clamping
// date-fns' addMonths performs, just computed from UTC fields instead of local ones.
const addUtcMonths = (date: Date, months: number): Date => {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();
  const targetMonthsTotal = y * 12 + m + months;
  const targetYear = Math.floor(targetMonthsTotal / 12);
  const targetMonth = targetMonthsTotal % 12;
  const daysInTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  const clampedDay = Math.min(d, daysInTargetMonth);
  return new Date(
    Date.UTC(
      targetYear,
      targetMonth,
      clampedDay,
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds(),
    ),
  );
};

const advance = (date: Date, frequency: "daily" | "weekly" | "monthly", interval: number): Date => {
  if (frequency === "weekly") return addUtcDays(date, interval * 7);
  if (frequency === "daily") return addUtcDays(date, interval);
  return addUtcMonths(date, interval);
};

/**
 * Build the list of instances for a recurring booking. Includes EVERY visible instance in the
 * window — past, excluded, and available — so the UI can render greyed-out rows for exclusions
 * and muted rows for past dates.
 *
 * Two windows:
 *  - **Bounded** recurrences (have `count` or `until`): walk from baseDate, capped at 52
 *    instances or the count/until — whichever comes first.
 *  - **Unbounded** recurrences (no count, no until): show the last 3 past occurrences for
 *    context, then the next 52 forward from today, capped by the 1-year forward look (R1).
 */
export function generateOccurrenceList(
  baseDate: Date,
  recurrence: OccurrenceRecurrenceInput,
  today: Date = new Date(),
): OccurrenceEntry[] {
  const frequency = recurrence.frequency ?? "weekly";
  const interval = recurrence.interval && recurrence.interval > 0 ? recurrence.interval : 1;
  const until = recurrence.until ? new Date(recurrence.until) : null;
  const count = recurrence.count && recurrence.count > 0 ? recurrence.count : null;
  const cancelledFrom = recurrence.cancelledFrom ? utcDayBounds(new Date(recurrence.cancelledFrom)).start : null;
  const exceptionSet = new Set(
    (recurrence.exceptions ?? []).map((d) => utcDayKey(new Date(d))),
  );
  const startOfToday = utcDayBounds(today).start;
  const oneYearAhead = addUtcDays(startOfToday, ONE_YEAR_DAYS);
  // A slot's `until` is stripped (R13); it is bounded ONLY by `cancelledFrom`. Keep the open-ended
  // windowing (last-3-past + forward) so slots still show a past tail, but stop the FORWARD walk at
  // cancelledFrom below so no occurrence appears on/after the termination date.
  const isUnbounded = !count && !until;
  // Occurrences on/after cancelledFrom must never appear.
  const stoppedByCancelledFrom = (d: Date): boolean =>
    cancelledFrom !== null && utcDayBounds(d).start.getTime() >= cancelledFrom.getTime();

  const buildEntry = (d: Date): OccurrenceEntry => {
    const iso = utcDayKey(d);
    const isPast = d.getTime() < startOfToday.getTime();
    const isExcluded = exceptionSet.has(iso);
    return { iso, date: new Date(d), state: isPast ? "past" : isExcluded ? "excluded" : "available" };
  };

  if (isUnbounded) {
    // Walk every occurrence from baseDate up to the 1-year forward cap; from that full list,
    // surface the last `PAST_TAIL_FOR_OPEN_ENDED` past entries plus up to `FORWARD_LOOKUP_LIMIT`
    // future entries.
    const past: OccurrenceEntry[] = [];
    const future: OccurrenceEntry[] = [];
    let current = new Date(baseDate);
    for (let i = 0; i < MAX_ITERATIONS; i++) {
      if (current.getTime() > oneYearAhead.getTime()) break;
      if (stoppedByCancelledFrom(current)) break; // series ends at the termination date
      const entry = buildEntry(current);
      if (entry.state === "past") {
        past.push(entry);
      } else {
        future.push(entry);
        if (future.length >= FORWARD_LOOKUP_LIMIT) break;
      }
      current = advance(current, frequency, interval);
    }
    const pastTail = past.slice(-PAST_TAIL_FOR_OPEN_ENDED);
    return [...pastTail, ...future];
  }

  // Bounded: keep prior behaviour — start at baseDate, stop at count or until.
  const cap = Math.min(FORWARD_LOOKUP_LIMIT, count ?? FORWARD_LOOKUP_LIMIT);
  const entries: OccurrenceEntry[] = [];
  let current = new Date(baseDate);
  for (let i = 0; i < cap; i++) {
    if (until && current.getTime() > until.getTime()) break;
    if (stoppedByCancelledFrom(current)) break; // series ends at the termination date
    entries.push(buildEntry(current));
    current = advance(current, frequency, interval);
  }
  return entries;
}
